import { useEffect, useState } from "react";
import { Navigate } from "react-router";
import { api } from "../api";
import { PocketFrame } from "../shell";
import { isRestrictedChild, useMe, type Me } from "../me";
import { offerLabel, paywallView, type PaywallOffer } from "./paywall-view";
import { PinUnlock } from "./PinGate";
import type { FamilyPayload } from "./types";

function formatRub(kopecks: string | number): string {
  return `${(Number(kopecks) / 100).toLocaleString("ru-RU")} ₽`;
}

export function Paywall() {
  const [me, setMe] = useMe();
  const [offers, setOffers] = useState<PaywallOffer[]>([]);
  const [learnerId, setLearnerId] = useState("");
  const [childName, setChildName] = useState("");
  const [out, setOut] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api<{ offers: PaywallOffer[] }>("/api/v1/public/storefront").then((d) => setOffers(d.offers)).catch(() => undefined);
    api<FamilyPayload>("/api/v1/family").then((f) => {
      setLearnerId(f.learners[0]?.id ?? "");
      setChildName(f.learners[0]?.nickname ?? "");
    }).catch(() => undefined);
  }, []);

  if (me === undefined) return <PocketFrame className="family-page"><p className="page">…</p></PocketFrame>;
  if (!me) return <Navigate to="/login?next=/family/paywall" />;

  const unlocked = me.parentUnlocked && !isRestrictedChild(me);
  const view = paywallView(unlocked, offers);

  return (
    <PocketFrame className="family-page">
    <div className="page">
      <h1>{view.title}</h1>
      {view.mode === "child" ? (
        <>
          <p>Твой прогресс сохранён.</p>
          <p>{view.body}</p>
          <div className="row">
            <span className="btn" aria-hidden="true">{view.cta}</span>
            <a className="btn ghost" href="/app/progress">Посмотреть прогресс</a>
          </div>
          {isRestrictedChild(me) ? null : <PinUnlock onUnlocked={(next: Me) => setMe(next)} />}
        </>
      ) : (
        <>
          <p>{childName ? `Прогресс ${childName} сохранён. Выберите вариант доступа, чтобы продолжить.` : view.body}</p>
          <p>История занятий и закреплённые навыки остаются на месте.</p>
          <p className="badge">Тестовая оплата</p>
          <p>Деньги не списываются, данные карты не спрашиваем.</p>
          {view.offers.map((o) => (
            <div key={o.id} className="card">
              <p>{offerLabel(o)}{o.title ? ` · ${o.title}` : ""}</p>
              <p>Указанная цена: {formatRub(o.price_kopecks)}</p>
              <p>Комиссия Clever: 20%. Доля репетитора: 80%.</p>
              {(["success", "failure", "cancel"] as const).map((outcome) => (
                <button
                  key={outcome}
                  type="button"
                  className="btn ghost"
                  onClick={async () => {
                    setErr("");
                    try {
                      const checkout = await api<{ checkoutId: string }>("/api/v1/commerce/checkouts", {
                        method: "POST",
                        body: JSON.stringify({ offerVersionId: o.id, learnerId, idempotencyKey: crypto.randomUUID() }),
                      });
                      const res = await api<{ order: { state: string } }>(`/api/v1/commerce/checkouts/${checkout.checkoutId}/mock-outcome`, {
                        method: "POST",
                        body: JSON.stringify({ outcome }),
                      });
                      setOut(res.order.state);
                    } catch (ex) {
                      setErr((ex as Error).message);
                    }
                  }}
                >
                  {outcome === "success" ? "Имитировать успешную оплату" : outcome === "failure" ? "Имитировать отказ" : "Отменить оформление"}
                </button>
              ))}
            </div>
          ))}
          {out === "succeeded" ? <p>Тестовый доступ включён. Деньги не списаны.</p> : null}
          {out === "failed" ? <p>Сценарий отказа выполнен. Доступ не изменился, деньги не списаны.</p> : null}
          {out === "canceled" ? <p>Оформление отменено. Доступ и прогресс не изменились.</p> : null}
          {err ? <p className="alert">{err}</p> : null}
        </>
      )}
    </div>
    </PocketFrame>
  );
}
